/**
 * Painel de leituras: temperatura, umidade, CO₂ e VPD do aparelho selecionado.
 *
 * Responsabilidade: mostrar o valor mais recente de cada sensor, a tendencia e
 * a sparkline da ULTIMA HORA.
 *
 * NAO e responsabilidade deste arquivo: buscar dado (`ws.ts`/`api.ts`) nem
 * decidir se o dado e velho — quem etiqueta idade e o cabecalho do app, uma
 * vez so, para a tela inteira.
 *
 * ## Duas fontes para o mesmo numero, e qual vence
 *
 * O mesmo tick de 60 s do `datalogger` alimenta a telemetria (§4) e o digital
 * twin (§5). A telemetria e a serie; o twin e retido e chega tambem na mudanca.
 * Aqui vence a ULTIMA amostra de telemetria quando ela existe, e o twin cobre
 * o resto — inclusive a faixa de VPD, que so o twin carrega.
 *
 * `null` vira "—" e nunca zero: o contrato §4 diz que campo de sensor pode vir
 * `null` quando a leitura nunca chegou, e um "0 °C" inventado seria pior do
 * que a ausencia.
 *
 * ## Por que a janela e ancorada em `Date.now()`, e nao na ultima amostra
 *
 * Um aparelho tres dias offline nao pode mostrar uma sparkline de "ha tres
 * dias" com o rotulo "ultima hora" embaixo — isso seria uma tendencia
 * inventada. Ancorar no relogio do NAVEGADOR faz a janela ficar vazia (e a
 * sparkline sumir) exatamente quando nao ha dado recente, e a idade real do
 * numero continua sendo dita, uma vez so, pelo cabecalho do app.
 */

import { formatar, formatarDelta, METRICAS, type Metrica } from "./../metricas";
import * as store from "./../store";
import type { TelemetrySample } from "./../tipos";
import { el, sparkline, trocar, type Painel } from "./../ui";

/** A janela que os cartoes mostram. */
const JANELA_S = 3600;
/** Quantos pontos de cada ponta a tendencia compara — resistente a um pico isolado. */
const PONTOS_TENDENCIA = 3;

type Seta = "up" | "down" | "flat";

export function montar(): Painel {
  const raiz = el("section", { class: "readings", id: "leituras" });

  const cartoes = METRICAS.map((metrica) => {
    const valor = el("span", { class: "r-numero", text: "—" });
    const tendencia = el("span", { class: "r-tend", hidden: true });
    const grafico = el("div", { class: "r-spark" });
    const janela = el("span", { class: "r-janela", text: "última hora", hidden: true });
    const alerta = el("span", { class: "warn-badge", text: "⚠", hidden: true });
    // `<button>` e nao um `<div>` com `onclick`: o cartao abre o grafico, entao
    // ele e um controle — e assim chega pelo teclado e se anuncia como tal, de
    // graca.
    const cartao = el(
      "button",
      {
        type: "button",
        class: "reading-card card",
        "aria-expanded": "false",
        onclick: () => store.abrirMetrica(metrica.chave),
      },
      el("div", { class: "r-label" }, metrica.rotulo, alerta),
      el(
        "div",
        { class: "r-value" },
        valor,
        el("span", { class: "r-unit", text: metrica.unidade }),
        tendencia,
      ),
      grafico,
      janela,
    );
    raiz.append(cartao);
    return { metrica, cartao, valor, tendencia, grafico, janela, alerta };
  });

  function atualizar(): void {
    const alvo = store.aparelhoSelecionado();
    const estado = store.obter();
    const amostras =
      alvo === null ? [] : (estado.telemetria[alvo.device.id] ?? []);
    const ultima = amostras.at(-1) ?? null;
    const sensor = alvo?.state?.sensor;
    const vpdForaDaFaixa =
      sensor?.vpd_status !== undefined && sensor.vpd_status !== "ok";

    const corte = Date.now() / 1000 - JANELA_S;
    const daUltimaHora = amostras.filter((a) => a.ts >= corte);

    const aberta = estado.metricaAberta;

    for (const { metrica, cartao, valor, tendencia, grafico, janela, alerta } of cartoes) {
      const daTelemetria = ultima === null ? null : metrica.de(ultima);
      const doTwin = sensor === undefined ? null : metrica.doTwin(sensor);
      valor.textContent = formatar(metrica, daTelemetria ?? doTwin);

      const linha = sparkline(daUltimaHora.map((a) => metrica.de(a)));
      trocar(grafico, linha);
      janela.hidden = linha === null;

      const t = calcularTendencia(metrica, daUltimaHora);
      if (t === null) {
        tendencia.hidden = true;
      } else {
        tendencia.hidden = false;
        tendencia.textContent =
          t.seta === "flat" ? "→" : `${t.seta === "up" ? "↑" : "↓"} ${formatarDelta(metrica, t.delta)}`;
      }

      const ehVpd = metrica.chave === "vpd";
      alerta.hidden = !(ehVpd && vpdForaDaFaixa);
      cartao.classList.toggle("warn", ehVpd && vpdForaDaFaixa);
      cartao.classList.toggle("aberta", metrica.chave === aberta);
      cartao.setAttribute("aria-expanded", String(metrica.chave === aberta));
    }
  }

  return { raiz, atualizar };
}

/**
 * Media dos `PONTOS_TENDENCIA` primeiros contra a dos `PONTOS_TENDENCIA`
 * ultimos valores validos da janela. Abaixo de `limiarTendencia`, "flat" —
 * ruido de sensor nao e tendencia.
 */
function calcularTendencia(
  metrica: Metrica,
  amostras: TelemetrySample[],
): { seta: Seta; delta: number } | null {
  const valores = amostras
    .map((a) => metrica.de(a))
    .filter((v): v is number => v !== null && Number.isFinite(v));
  if (valores.length < 2) return null;

  const n = Math.min(PONTOS_TENDENCIA, valores.length);
  const inicio = media(valores.slice(0, n));
  const fim = media(valores.slice(-n));
  const delta = fim - inicio;
  if (Math.abs(delta) < metrica.limiarTendencia) return { seta: "flat", delta };
  return { seta: delta > 0 ? "up" : "down", delta };
}

const media = (valores: number[]): number =>
  valores.reduce((soma, v) => soma + v, 0) / valores.length;
